import { FC, useCallback, useMemo, useState } from 'react';
import { selectIdentity } from '../../../../store/entities/identity';
import { QcsTableCell } from '../../../common/basic/QcsTableCell';
import { GridList } from '../../../common/grid/GridList';
import { useAppSelector } from '../../../../store';
import { QcsLoadingButton } from '../../../common/basic/QcsLoadingButton';
import { downloadBlob } from '../../../../utils/download';
import { WorkplaceRiskModal } from './WorkplaceRiskModal';
import { WorkplaceSafetyEquipmentModal } from './WorkplaceSafetyEquipmentModal';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { InnerTooltipWrapper, QcsTooltip } from '../../../common/basic/QcsTooltip';
import { SectionHeader } from '../../../common/SectionHeader';
import { WorkplaceDeleteModal } from './WorkplaceDeleteModal';
import { WorkplaceAddModal } from './WorkplaceAddModal';
import { faCircleExclamation, faHelmetSafety, faRoadBarrier } from '@fortawesome/free-solid-svg-icons';
import { FaIcon } from '../../../common/FaIcon';
import { SxProps, Theme, useTheme } from '@mui/material';
import { hasRole, isExternal } from '../../../../utils/roles';
import { getLangNameObj } from '../../../../utils/format';
import { useInvitation } from './invitationFunctions';
import { SafetyEquipmentType } from '@api/models/enums/SafetyEquipmentType';
import { UserRole } from '@api/models/enums/UserRole';
import { InvitationRelationResponse } from '@api/models/invitation/InvitationRelationResponse';
import { InvitationState } from '@api/models/enums/InvitationState';
import { InvitationDocsExportType } from '@api/models/enums/InvitationDocsExportType';
import { invitationService } from '@api/services/services';
import { resources } from '@api/resources';

interface WorkplaceTableData extends InvitationRelationResponse {
  id?: string;
}

export const InvTabWorkplaces: FC = () => {
  const { t, i18n, invitation } = useInvitation();
  const identity = useAppSelector(selectIdentity);
  const [riskModal, setRiskModal] = useState<WorkplaceTableData>();
  const [workplaceAddModal, setWorkplaceAddModal] = useState<boolean>(false);
  const [workplaceToDelete, setWorkplaceToDelete] = useState<WorkplaceTableData>();
  const [safetyEquipmentModal, setSafetyEquipmentModal] = useState<WorkplaceTableData>();
  const [checkedItems, setCheckedItems] = useState<WorkplaceTableData[]>([]);
  const theme = useTheme();
  const list = useMemo(
    (): WorkplaceTableData[] => (invitation.workplaces ?? []).map((x) => ({ ...x, id: x.workplace?.id })),
    [invitation.workplaces],
  );

  const hasNoAssignedPerson = useCallback(
    (item: WorkplaceTableData) =>
      (invitation.persons ?? []).length === 0 || !invitation.persons!.some((person) => person.workplaces.some((w) => w.id === item.workplace?.id)),
    [invitation.persons],
  );

  const handleGetRowSx = useCallback(
    (item: WorkplaceTableData): SxProps<Theme> | undefined => {
      if (hasNoAssignedPerson(item)) {
        return { boxShadow: `inset 4px 0 0 0 ${theme.palette.warning.main}` };
      }
      return undefined;
    },
    [hasNoAssignedPerson, theme],
  );

  const checkedItemsForDownload = useMemo(() => checkedItems.filter((item) => (item.risks ?? []).some((risk) => !!risk.file?.id)), [checkedItems]);

  const getRequiredAction = (item: WorkplaceTableData) => {
    if (isExternal(identity) && (item.risks?.length ?? 0) >= 1 && (item.risks ?? []).some((risk) => !risk.approvedOn)) {
      return t('invitation.workplace.requiredActionApproveRisks');
    }

    return '';
  };

  const handleRenderData = (item: WorkplaceTableData) => {
    const showRiskDisabled = (item.risks?.length ?? 0) < 1;
    const showRiskWarning = !showRiskDisabled && (item.risks ?? []).some((risk) => !risk.approvedOn);
    const showSafetyEquipmentDisabled = (item.safetyEquipments?.length ?? 0) < 1;
    const showSafetyEquipmentOk = !showSafetyEquipmentDisabled && !(item.safetyEquipments ?? []).some((x) => x.type === SafetyEquipmentType.Owned);
    const deleteDisabled =
      invitation.state === InvitationState.Archived ||
      !hasRole(identity.roles, [UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.AdminQcs]) ||
      //At least one workplace must left.
      (invitation.workplaces ?? []).length < 2 ||
      //After delete no person with no workplace.
      invitation.persons?.some(
        (person) => person.workplaces?.some((workplace) => workplace.id === item.workplace?.id) === true && person.workplaces.length === 1,
      ) === true;

    return (
      <>
        <QcsTableCell>
          {getLangNameObj(i18n, item.workplace)}
          {hasNoAssignedPerson(item) && (
            <QcsTooltip title={t('invitation.workplace.noWorkersAssigned')} placement="top">
              <InnerTooltipWrapper>
                <FaIcon icon={faCircleExclamation} color={theme.palette.warning.main} style={{ marginLeft: '0.5rem', verticalAlign: 'middle' }} />
              </InnerTooltipWrapper>
            </QcsTooltip>
          )}
        </QcsTableCell>
        <QcsTableCell hideOnMobile={true}>{getLangNameObj(i18n, item.establishment)}</QcsTableCell>
        {isExternal(identity) && <QcsTableCell>{getRequiredAction(item)}</QcsTableCell>}
        <QcsTableCell align="right">
          {/* SHOW RISK WORKPLACE */}
          <QcsTooltip title={t('invitation.workplace.riskTitle')} placement="top">
            <InnerTooltipWrapper>
              <QcsLoadingButton
                onClick={handleShowRisk(item)}
                resourceLoading={[resources.approveRisks, resources.generateZipFile]}
                disabled={showRiskDisabled}
              >
                <FaIcon
                  icon={faRoadBarrier}
                  color={showRiskDisabled ? undefined : showRiskWarning ? theme.palette.warning.main : theme.palette.success.main}
                />
              </QcsLoadingButton>
            </InnerTooltipWrapper>
          </QcsTooltip>

          {/* SAFETY EQUIPMENT */}
          <QcsTooltip title={t('invitation.workplace.safetyEquipmentTitle')} placement="top">
            <InnerTooltipWrapper>
              <QcsLoadingButton
                onClick={showSafetyEquipment(item)}
                resourceLoading={[resources.approveRisks, resources.generateZipFile]}
                disabled={showSafetyEquipmentDisabled}
              >
                <FaIcon
                  icon={faHelmetSafety}
                  color={showSafetyEquipmentDisabled ? undefined : showSafetyEquipmentOk ? theme.palette.success.main : theme.palette.warning.main}
                />
              </QcsLoadingButton>
            </InnerTooltipWrapper>
          </QcsTooltip>

          {/* DELETE WORKPLACE */}
          <QcsTooltip title={t('workplace.deleteWorkplace')} placement="top">
            <InnerTooltipWrapper>
              <QcsLoadingButton
                onClick={handleDeleteWorkplace(item)}
                resourceLoading={[resources.approveRisks, resources.generateZipFile]}
                disabled={deleteDisabled}
              >
                <DeleteForeverIcon color={deleteDisabled ? undefined : 'error'} />
              </QcsLoadingButton>
            </InnerTooltipWrapper>
          </QcsTooltip>
        </QcsTableCell>
      </>
    );
  };

  const handleChangeChecked = (items: WorkplaceTableData[]) => {
    setCheckedItems(items);
  };

  const handleItemToSelectObject = (item: WorkplaceTableData) => item;

  const handleSelectObjectGetId = (item: WorkplaceTableData) => item.id!;

  const handleIsCheckDisabled = (item: WorkplaceTableData) => !(item.risks ?? []).some((risk) => !risk.approvedOn || !!risk.file?.id);

  const handleDownload = async () => {
    const items = checkedItemsForDownload;
    if (!items) return;

    const res = await invitationService.generateZipFile(
      invitation.id!,
      items.map((x) => x.id!),
      InvitationDocsExportType.WorkplaceRisks,
    );
    await downloadBlob(res);
  };

  const handleShowRisk = (item: WorkplaceTableData) => (event: React.SyntheticEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setRiskModal(item);
  };

  const handleCloseRisk = () => {
    setRiskModal(undefined);
  };

  const showSafetyEquipment = (item: WorkplaceTableData) => (event: React.SyntheticEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setSafetyEquipmentModal(item);
  };

  const handleCloseSafetyEquipment = () => {
    setSafetyEquipmentModal(undefined);
  };

  const handleDeleteWorkplace = (item: WorkplaceTableData) => (event: React.SyntheticEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setWorkplaceToDelete(item);
  };

  const handleCloseDeleteWorkplace = () => {
    setWorkplaceToDelete(undefined);
  };

  const handleAddWorkplace = () => {
    setWorkplaceAddModal(true);
  };

  const handleCloseAddWorkplace = () => {
    setWorkplaceAddModal(false);
  };

  const showAddButton = hasRole(identity.roles, [UserRole.AdminQcs, UserRole.ManagerOsah, UserRole.ManagerWorkplace]);

  return (
    <>
      <WorkplaceRiskModal workplaceId={riskModal?.workplace?.id} onClose={handleCloseRisk} />
      <WorkplaceSafetyEquipmentModal item={safetyEquipmentModal} onClose={handleCloseSafetyEquipment} />
      <WorkplaceDeleteModal workplace={workplaceToDelete} onClose={handleCloseDeleteWorkplace} />
      <WorkplaceAddModal open={workplaceAddModal} onClose={handleCloseAddWorkplace} />

      {showAddButton && (
        <SectionHeader
          title=" "
          addText="workplace.addWorkplace"
          handleAddClick={handleAddWorkplace}
          addDisabled={invitation.state === InvitationState.Archived}
        />
      )}
      <GridList<WorkplaceTableData, WorkplaceTableData>
        headers={[
          { captionStr: 'invitation.workplace.name' },
          {
            captionStr: 'invitation.workplace.establishment',
            hideOnMobile: true,
          },
          ...(isExternal(identity) ? [{ captionStr: 'invitation.workplace.requiredAction' }] : []),
          {},
        ]}
        data={list}
        renderData={handleRenderData}
        getRowSx={handleGetRowSx}
        search={false}
        hidePagination={true}
        checkbox={true}
        checkedItems={checkedItems}
        changeChecked={handleChangeChecked}
        itemToSelectObject={handleItemToSelectObject}
        selectObjectGetId={handleSelectObjectGetId}
        isCheckDisabled={handleIsCheckDisabled}
      />

      <QcsLoadingButton
        variant="contained"
        onClick={handleDownload}
        disabled={checkedItemsForDownload.length === 0}
        resourceLoading={[resources.approveRisks, resources.generateZipFile]}
        sx={{ mt: '1.5rem' }}
      >
        {t('invitation.workplace.download')}
      </QcsLoadingButton>
    </>
  );
};
